import React, { useState, useEffect, useRef } from 'react';

interface SpeechBubble {
  id: number;
  text: string;
  startX: number; // percentage from left
  startY: number; // percentage from top
  delay: number; // animation delay in ms
  points: number; // points badge value
  badgeColor: string;
  badgeInk: string;
}

const SAMPLE_PHRASES: string[] = [
  "What's your take on the latest industry trends?",
  "Share your expert perspective…",
  "What story do you want to tell?",
  "How do you see the future shaping up?",
  "What insights can you share today?",
];

// Badge palette pulled from light-ui.tsx (var(--lp-*) palette). Each bubble
// gets its own colour so the badges echo the conic gradient ring around the
// paper-disc mark used elsewhere in the light UI.
const BADGE_PALETTE: Array<{ bg: string; ink: string }> = [
  { bg: '#ec4899', ink: '#ffffff' }, // pink
  { bg: '#8b5cf6', ink: '#ffffff' }, // violet
  { bg: '#f97316', ink: '#ffffff' }, // orange
  { bg: '#f59e0b', ink: '#3a2a05' }, // amber
  { bg: '#14b8a6', ink: '#ffffff' }, // mint
];

const BUBBLE_POSITIONS: Array<Omit<SpeechBubble, 'id' | 'text' | 'badgeColor' | 'badgeInk'>> = [
  { startX: 10, startY: 20, delay: 0, points: 2 },
  { startX: 75, startY: 15, delay: 300, points: 4 },
  { startX: 5, startY: 65, delay: 600, points: 2 },
  { startX: 80, startY: 70, delay: 900, points: 4 },
  { startX: 15, startY: 45, delay: 1200, points: 2 },
];

interface SpeechBubbleAnimationProps {
  onComplete?: () => void;
}

// Inline triangle tail that picks up the paper-2 background so it blends
// seamlessly with the bubble card.
const TailSVG: React.FC = () => (
  <svg
    viewBox="0 0 36 36"
    width="36"
    height="36"
    aria-hidden="true"
    style={{ display: 'block' }}
  >
    <path
      d="M5 2 L20 2 L8 22 C6.6 24.3 3.2 22.5 4 20 Z"
      fill="var(--lp-paper-2, #f6f5ee)"
      stroke="rgba(40,50,40,0.10)"
      strokeWidth="1"
    />
  </svg>
);

const Bubble: React.FC<{ bubble: SpeechBubble }> = ({ bubble }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [animationState, setAnimationState] = useState<'waiting' | 'visible' | 'flying' | 'done'>('waiting');

  useEffect(() => {
    const showTimer = setTimeout(() => setAnimationState('visible'), bubble.delay);
    const flyTimer = setTimeout(() => setAnimationState('flying'), bubble.delay + 800);
    const doneTimer = setTimeout(() => setAnimationState('done'), bubble.delay + 1600);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(flyTimer);
      clearTimeout(doneTimer);
    };
  }, [bubble.delay]);

  if (animationState === 'done') return null;

  const translateX = 50 - bubble.startX;
  const translateY = 50 - bubble.startY;

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        left: `${bubble.startX}%`,
        top: `${bubble.startY}%`,
        minWidth: 200,
        maxWidth: 300,
        padding: '14px 18px',
        background: 'var(--lp-paper-2, #f6f5ee)',
        border: '1px solid rgba(40,50,40,0.10)',
        borderRadius: '2px 22px 2px 22px',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.85) inset, 0 18px 40px -16px rgba(40,50,40,0.22), 0 6px 18px -8px rgba(40,50,40,0.10)',
        opacity: animationState === 'waiting' ? 0 : animationState === 'visible' ? 1 : 0,
        transform:
          animationState === 'waiting'
            ? 'translate(-50%, -50%) scale(0.6)'
            : animationState === 'visible'
              ? 'translate(-50%, -50%) scale(1)'
              : `translate(calc(-50% + ${translateX}vw), calc(-50% + ${translateY}vh)) scale(0.2)`,
        transition: 'opacity 700ms ease-out, transform 700ms ease-out',
      }}
    >
      {/* Points badge */}
      <div
        style={{
          position: 'absolute',
          top: -10,
          right: -10,
          zIndex: 20,
          minWidth: 30,
          height: 30,
          padding: '0 8px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 999,
          background: bubble.badgeColor,
          color: bubble.badgeInk,
          fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          boxShadow:
            '0 1px 0 rgba(255,255,255,0.55) inset, 0 4px 10px -4px rgba(40,50,40,0.30)',
          border: '1.5px solid rgba(255,255,255,0.85)',
        }}
      >
        +{bubble.points}
      </div>

      {/* Triangle tail */}
      <div
        style={{
          position: 'absolute',
          bottom: -28,
          left: '50%',
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
        }}
      >
        <TailSVG />
      </div>

      <span
        style={{
          position: 'relative',
          zIndex: 10,
          display: 'block',
          color: 'var(--lp-ink, #1a1f1a)',
          fontFamily: "'Fraunces', 'Times New Roman', serif",
          fontSize: 15,
          lineHeight: 1.25,
          textAlign: 'center',
          textWrap: 'balance',
        }}
      >
        {bubble.text}
      </span>
    </div>
  );
};

const SpeechBubbleAnimation: React.FC<SpeechBubbleAnimationProps> = ({ onComplete }) => {
  const [bubbles, setBubbles] = useState<SpeechBubble[]>([]);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    const shuffledPhrases = [...SAMPLE_PHRASES].sort(() => Math.random() - 0.5);
    const paletteOffset = Math.floor(Math.random() * BADGE_PALETTE.length);
    const initialBubbles = BUBBLE_POSITIONS.map((pos, index) => {
      const swatch = BADGE_PALETTE[(index + paletteOffset) % BADGE_PALETTE.length];
      return {
        ...pos,
        id: index,
        text: shuffledPhrases[index % shuffledPhrases.length],
        badgeColor: swatch.bg,
        badgeInk: swatch.ink,
      };
    });
    setBubbles(initialBubbles);

    const totalDuration = 1200 + 1600 + 300;
    const timer = setTimeout(() => {
      setIsAnimating(false);
      onComplete?.();
    }, totalDuration);

    return () => clearTimeout(timer);
  }, [onComplete]);

  if (!isAnimating) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {bubbles.map((bubble) => (
        <Bubble key={bubble.id} bubble={bubble} />
      ))}
    </div>
  );
};

export default SpeechBubbleAnimation;
